import { useCallback, useEffect, useRef } from 'react';

const openPopup = (url: string, title: string, h: number, w: number) => {
  const left = window.screen.width / 2 - w / 2;
  const top = window.screen.height / 2 - h / 2;
  const popup = window.open(
    url,
    title,
    `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${w}, height=${h}, top=${top}, left=${left}`,
  );
  return popup;
};

type UsePopup = (
  url: string,
  title: string,
  onClose?: VoidFunction,
  onOpen?: VoidFunction,
  height?: number,
  width?: number,
) => {
  cancelOnClose: () => void;
  clearPopupInterval: () => void;
  createPopup: () => void;
};

/**
 * Create a popup that will detect when it is closed and call an onClose handler.
 *
 * If the component in which this hook is called unmounts before closure is
 * detected, the onClose callback will not be called.
 *
 * @param url - url the popup will open
 * @param title - the title of the popup
 * @param onClose - will be called when the popup is closed unless cancelOnClose
 *  is called first.
 * @param onOpen - Is called when the createPopup is called.
 * @param height - height dimension of the popup in pixels.
 * @param width - width dimension of the popup in pixels.
 *
 * @returns
 *   @param cancelOnClose - prevent the onClose handler from being called.
 *   @param clearPopupInterval - clears the setInterval that checks for popup closure.
 *   @param createPopup - creates the popup.
 */
const usePopup: UsePopup = (
  url,
  title,
  onClose,
  onOpen,
  height = 750,
  width = 700,
) => {
  const timerRef = useRef<NodeJS.Timer | null>(null);
  const canOnCloseRef = useRef(true);

  const cancelOnClose = useCallback(() => {
    canOnCloseRef.current = false;
  }, []);

  const clearPopupInterval = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const createPopup = useCallback(() => {
    if (typeof onOpen === 'function') {
      onOpen();
    }
    const popup = openPopup(url, title, height, width);
    timerRef.current = setInterval(() => {
      if (popup && popup.closed && canOnCloseRef.current === true) {
        if (typeof onClose === 'function') {
          onClose();
        }
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        timerRef.current = null;
      }
    }, 100);
  }, [height, width, onClose, onOpen, title, url]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  return {
    clearPopupInterval,
    cancelOnClose,
    createPopup,
  };
};

export default usePopup;
